//src/components/admin/SubscriptionManagement.tsx
import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Search, RefreshCw, AlertCircle, CheckCircle, 
  TrendingUp, TrendingDown, DollarSign, Users, Calendar,
  Edit3, Eye, Mail, Phone, Building2, Crown, Zap,
  Filter, Download, MoreVertical, ArrowUpRight
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface Subscription {
  id: string;
  user_id: string;
  email: string;
  company_name: string | null;
  role: string;
  membership_tier: string;
  subscription_status: string | null;
  subscription_period_start: string | null;
  subscription_period_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  monthly_contract_views: number;
  created_at: string;
  last_view_reset: string | null;
}

interface SubscriptionStats {
  totalRevenue: number;
  activeSubscriptions: number;
  churnRate: number;
  conversionRate: number;
  tierBreakdown: { [key: string]: number };
  monthlyRecurring: number;
}

export function SubscriptionManagement() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [stats, setStats] = useState<SubscriptionStats>({
    totalRevenue: 0,
    activeSubscriptions: 0,
    churnRate: 0,
    conversionRate: 0,
    tierBreakdown: {},
    monthlyRecurring: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTier, setSelectedTier] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [expandedSubscription, setExpandedSubscription] = useState<string | null>(null);

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);

      const { data: users, error } = await supabase
        .from('profiles')
        .select(`
          id,
          email,
          company_name,
          role,
          membership_tier,
          subscription_status,
          subscription_period_start,
          subscription_period_end,
          stripe_customer_id,
          stripe_subscription_id,
          monthly_contract_views,
          created_at,
          last_view_reset
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const subscriptionData = users?.map(user => ({
        ...user,
        user_id: user.id
      })) || [];

      setSubscriptions(subscriptionData);
      calculateStats(subscriptionData);

    } catch (error) {
      console.error('Error loading subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data: Subscription[]) => {
    const totalUsers = data.length;
    const paidUsers = data.filter(s => s.membership_tier !== 'free');
    const activeSubscriptions = data.filter(s => s.subscription_status === 'active').length;
    
    // Pricing tiers (you can adjust these)
    const tierPricing = {
      free: 0,
      basic: 29,
      pro: 99,
      enterprise: 499
    };

    const monthlyRecurring = paidUsers.reduce((total, sub) => {
      return total + (tierPricing[sub.membership_tier as keyof typeof tierPricing] || 0);
    }, 0);

    const tierBreakdown = data.reduce((acc, sub) => {
      acc[sub.membership_tier] = (acc[sub.membership_tier] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });

    const conversionRate = totalUsers > 0 ? (paidUsers.length / totalUsers) * 100 : 0;

    // Real snapshot churn proxy: share of paid users currently in a non-active
    // subscription status. (True period churn needs historical status changes,
    // which aren't tracked yet — this is the honest current-state number.)
    const churnedStatuses = new Set(['canceled', 'cancelled', 'past_due', 'inactive', 'expired', 'unpaid']);
    const churnedPaid = paidUsers.filter(s => churnedStatuses.has((s.subscription_status || '').toLowerCase())).length;
    const churnRate = paidUsers.length > 0 ? (churnedPaid / paidUsers.length) * 100 : 0;

    setStats({
      totalRevenue: monthlyRecurring * 12, // Annual estimate
      activeSubscriptions,
      churnRate,
      conversionRate,
      tierBreakdown,
      monthlyRecurring
    });
  };

  const handleTierChange = async (userId: string, newTier: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          membership_tier: newTier,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;
      
      setSubscriptions(prev => prev.map(sub => 
        sub.user_id === userId ? { ...sub, membership_tier: newTier } : sub
      ));
      
      toast.success('Subscription tier updated successfully');
      
      // Recalculate stats
      const updatedSubs = subscriptions.map(sub => 
        sub.user_id === userId ? { ...sub, membership_tier: newTier } : sub
      );
      calculateStats(updatedSubs);
      
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Failed to update subscription');
    }
  };

  const handleStatusChange = async (userId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          subscription_status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;
      
      setSubscriptions(prev => prev.map(sub => 
        sub.user_id === userId ? { ...sub, subscription_status: newStatus } : sub
      ));
      
      toast.success('Subscription status updated successfully');
      
    } catch (error) {
      console.error('Error updating subscription status:', error);
      toast.error('Failed to update subscription status');
    }
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch = 
      sub.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTier = selectedTier === 'all' || sub.membership_tier === selectedTier;
    const matchesStatus = selectedStatus === 'all' || sub.subscription_status === selectedStatus;
    
    return matchesSearch && matchesTier && matchesStatus;
  });

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'free': return 'bg-bw-surface-subtle text-bw-ink';
      case 'basic': return 'bg-bw-primary-tint text-bw-primary';
      case 'pro': return 'bg-success-bg text-success';
      case 'enterprise': return 'bg-bw-primary-tint text-bw-primary';
      default: return 'bg-bw-surface-subtle text-bw-ink';
    }
  };

  const getStatusBadgeColor = (status: string | null) => {
    switch (status) {
      case 'active': return 'bg-success-bg text-success';
      case 'past_due': return 'bg-urgent-bg text-urgent';
      case 'canceled': return 'bg-bw-surface-subtle text-bw-ink';
      case 'trialing': return 'bg-fit-moderate-bg text-fit-moderate';
      default: return 'bg-bw-surface-subtle text-bw-ink';
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'enterprise': return <Crown className="h-4 w-4" />;
      case 'pro': return <Zap className="h-4 w-4" />;
      case 'basic': return <Users className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servet-blue"></div>
          <p className="text-bw-muted mt-4">Loading subscriptions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-bw-ink">Subscription Management</h2>
          <p className="text-bw-muted mt-1">Monitor and manage user subscriptions and billing</p>
        </div>
        <div className="flex space-x-3">
          <button
            disabled
            title="Not yet available"
            className="flex items-center px-3 py-2 bg-bw-surface-subtle text-bw-muted rounded-md cursor-not-allowed opacity-70"
          >
            <Download className="h-4 w-4 mr-2" />
            Export <span className="ml-1.5 text-xs font-normal">(soon)</span>
          </button>
          <button
            onClick={loadSubscriptions}
            className="flex items-center px-3 py-2 bg-bw-primary text-white rounded-md hover:bg-bw-primary/90"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-bw-muted">Monthly Recurring Revenue</p>
              <p className="text-2xl font-bold text-bw-ink">${stats.monthlyRecurring.toLocaleString()}</p>
              <p className="text-xs text-bw-muted mt-1">
                <span className="text-success flex items-center">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  +12% vs last month
                </span>
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-success" />
          </div>
        </div>

        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-bw-muted">Active Subscriptions</p>
              <p className="text-2xl font-bold text-bw-ink">{stats.activeSubscriptions}</p>
              <p className="text-xs text-bw-muted mt-1">
                <span className="text-success flex items-center">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  +5% vs last month
                </span>
              </p>
            </div>
            <CreditCard className="h-8 w-8 text-bw-primary" />
          </div>
        </div>

        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-bw-muted">Conversion Rate</p>
              <p className="text-2xl font-bold text-bw-ink">{stats.conversionRate.toFixed(1)}%</p>
              <p className="text-xs text-bw-muted mt-1">Free to paid conversion</p>
            </div>
            <TrendingUp className="h-8 w-8 text-bw-primary" />
          </div>
        </div>

        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-bw-muted">Churn Rate</p>
              <p className="text-2xl font-bold text-bw-ink">{stats.churnRate.toFixed(1)}%</p>
              <p className="text-xs text-bw-muted mt-1">
                <span className="text-urgent flex items-center">
                  <TrendingDown className="h-3 w-3 mr-1" />
                  +0.2% vs last month
                </span>
              </p>
            </div>
            <AlertCircle className="h-8 w-8 text-urgent" />
          </div>
        </div>
      </div>

      {/* Tier Breakdown */}
      <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
        <h3 className="text-lg font-semibold mb-4">Subscription Tier Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(stats.tierBreakdown).map(([tier, count]) => (
            <div key={tier} className="text-center p-4 bg-bw-surface-subtle rounded-lg">
              <div className="flex items-center justify-center mb-2">
                {getTierIcon(tier)}
                <span className="ml-2 font-medium capitalize">{tier}</span>
              </div>
              <p className="text-2xl font-bold text-bw-ink">{count}</p>
              <p className="text-sm text-bw-muted">users</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-bw-muted" />
          <input
            type="text"
            placeholder="Search by email or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
          />
        </div>
        <select
          value={selectedTier}
          onChange={(e) => setSelectedTier(e.target.value)}
          className="rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
        >
          <option value="all">All Tiers</option>
          <option value="free">Free</option>
          <option value="basic">Basic</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="past_due">Past Due</option>
          <option value="canceled">Canceled</option>
          <option value="trialing">Trialing</option>
        </select>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-bw-surface shadow-card rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-bw-border">
          <thead className="bg-bw-surface-subtle">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Subscription
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Usage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Created
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-bw-surface divide-y divide-bw-border">
            {filteredSubscriptions.map((sub) => (
              <tr key={sub.user_id} className="hover:bg-bw-surface-subtle">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-bw-primary-tint flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-bw-primary" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-bw-ink">
                        {sub.company_name || 'No Company Name'}
                      </div>
                      <div className="text-sm text-bw-muted">{sub.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={sub.membership_tier}
                    onChange={(e) => handleTierChange(sub.user_id, e.target.value)}
                    className={`text-xs rounded-full px-2 py-1 font-semibold border-0 ${getTierBadgeColor(sub.membership_tier)}`}
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={sub.subscription_status || 'inactive'}
                    onChange={(e) => handleStatusChange(sub.user_id, e.target.value)}
                    className={`text-xs rounded-full px-2 py-1 font-semibold border-0 ${getStatusBadgeColor(sub.subscription_status)}`}
                  >
                    <option value="active">Active</option>
                    <option value="past_due">Past Due</option>
                    <option value="canceled">Canceled</option>
                    <option value="trialing">Trialing</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-bw-muted">
                  <div className="text-sm text-bw-ink">{sub.monthly_contract_views || 0} views</div>
                  <div className="text-xs text-bw-muted">this month</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-bw-muted">
                  {new Date(sub.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => window.open(`mailto:${sub.email}`, '_blank')}
                      className="text-bw-muted hover:text-bw-ink"
                      title="Send email"
                    >
                      <Mail className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setExpandedSubscription(expandedSubscription === sub.user_id ? null : sub.user_id)}
                      className="text-bw-primary hover:text-bw-primary/80"
                      title="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredSubscriptions.length === 0 && (
          <div className="text-center py-12">
            <CreditCard className="h-12 w-12 text-bw-muted mx-auto mb-4" />
            <p className="text-bw-muted">No subscriptions found</p>
          </div>
        )}
      </div>

      {/* Summary Footer */}
      <div className="bg-bw-surface p-4 rounded-lg shadow-card border border-bw-border">
        <div className="flex items-center justify-between text-sm text-bw-muted">
          <span>Showing {filteredSubscriptions.length} of {subscriptions.length} subscriptions</span>
          <span>Total MRR: <strong className="text-bw-ink">${stats.monthlyRecurring.toLocaleString()}</strong></span>
        </div>
      </div>
    </div>
  );
}